/**
 * PWA-4 — sync-queue runtime: storage selection, executors, the drainer, and the
 * React subscription the offline indicator reads.
 *
 * The pure rules (ordering, backoff, permanent-vs-retryable) live in
 * `lib/syncQueue/queue.ts` and are unit-tested there. This module is the part
 * that touches the browser and Firebase.
 *
 * Import direction is one-way — `services/timesheets.ts` knows nothing about the
 * queue; the queue-aware wrappers at the bottom of this file are what the worker
 * UI calls. That keeps admin-side callers on the plain, non-queued path.
 */

import { doc, getDoc, waitForPendingWrites } from 'firebase/firestore';
import { useEffect, useState } from 'react';

import {
  createIdbStorage,
  isIdbAvailable,
  requestPersistentStorage,
} from '../lib/syncQueue/idbStorage';
import { createMemoryStorage } from '../lib/syncQueue/memoryStorage';
import {
  ownedQueueStorage,
  queueOwner,
  recoverLegacyActions,
  withoutMedia,
} from '../lib/syncQueue/ownedStorage';
import {
  drainQueue,
  enqueue,
  failedActions,
  isPermanentFailure,
  pendingActions,
  retryFailed,
  type EnqueueInput,
} from '../lib/syncQueue/queue';
import type { ExecutorMap, QueuedAction, SyncQueueStorage } from '../lib/syncQueue/types';

import type { ReportWorkExceptionInput } from '@rendimees24/shared-types';

import { getFirebase } from './firebase';
import { requestLeave, type RequestLeaveInput } from './leaveRequests';
import {
  completeWorkDay,
  startWorkDay,
  uploadTimesheetPhoto,
  requestTimeCorrection,
} from './timesheets';
import { newClientRequestId, reportWorkException } from './workerExceptions';
import { postWorkerMessage, type WorkerMessageInput } from './workerMessages';
import { submitWorkerSubmission, type SubmissionInput } from './workerSubmissions';

/**
 * Where a completion's `endedAt` came from (stage 1A-a, B1/Q51). 'chosen' is
 * the worker picking an earlier end in the finish panel because they forgot
 * to finish on time: the server keeps it inside its usual bounds and marks
 * the day for review (`worker_chosen_end`).
 */
export type EndedAtSource = NonNullable<Parameters<typeof completeWorkDay>[0]['endedAtSource']>;

export type CompleteWorkDayQueuedInput = Omit<Parameters<typeof completeWorkDay>[0], 'preview'>;

let storage: SyncQueueStorage | null = null;
let mediaEpoch = 0;
let loggingOut = false;
const discarded = new Set<string>();

function baseStorage(): SyncQueueStorage {
  if (!storage) {
    storage = isIdbAvailable() ? createIdbStorage() : createMemoryStorage();
  }
  return storage;
}

function getStorage(): SyncQueueStorage {
  const scoped = ownedQueueStorage(baseStorage(), getFirebase().auth.currentUser?.uid);
  const epoch = mediaEpoch;
  return {
    ...scoped,
    async put(action) {
      if (discarded.has(action.id)) return;
      const safe = epoch === mediaEpoch ? action : withoutMedia(action);
      if (safe) await scoped.put(safe);
    },
  };
}

function newId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// ─── Executors ──────────────────────────────────────────────────────────────
// Each replays the SAME service call the online path uses, so the server sees
// no difference between a live action and one that synced an hour later.

/**
 * Exported so the replay path can be tested directly. What an executor does
 * with an OLD queue entry is not visible from `completeWorkDayQueued`, and
 * that is exactly where a stale action can silently acquire today's date.
 */
export const executors: ExecutorMap = {
  requestTimeCorrection: async (action) => {
    await requestTimeCorrection(action.payload as Parameters<typeof requestTimeCorrection>[0]);
  },
  submitWorkerSubmission: async (action) => {
    const file = action.blob
      ? new File([action.blob], action.blobName ?? 'receipt', {
          type: action.blobType ?? 'image/jpeg',
        })
      : undefined;
    await submitWorkerSubmission(action.payload as unknown as SubmissionInput, file);
  },
  startWorkDay: async (action) => {
    const payload = action.payload as Parameters<typeof startWorkDay>[0];
    // Same stand-in rule as the completion: an action queued before start
    // times existed is replayed at the moment it was queued, marked as a
    // stand-in rather than passing as a measured start.
    const r = await startWorkDay(
      payload.startedAt
        ? { ...payload, startedAtSource: 'tap' }
        : { ...payload, startedAt: action.createdAt, startedAtSource: 'queue' },
    );
    // Handed back to the drainer, which fills it into the completion and the
    // photos that were queued against this day before it had a server id.
    return { timesheetId: r.timesheetId };
  },
  completeWorkDay: async (action) => {
    const payload = {
      ...action.payload,
      expectedWorkerId: action.ownerUid ?? action.payload.expectedWorkerId,
    } as CompleteWorkDayQueuedInput;
    // An action queued before end times existed carries none. Replaying it
    // bare would record the day as ending whenever the connection came back
    // — the very defect the field was added for. The queue already knows
    // when the worker acted, so that stands in, marked as a stand-in: the
    // server records it as source 'queue', not as a measured completion.
    // A time the worker CHOSE travels as chosen (1A-a): replaying it as a
    // tap would hide from the reviewer that the worker typed it.
    await completeWorkDay(
      payload.endedAt
        ? { ...payload, endedAtSource: payload.endedAtSource === 'chosen' ? 'chosen' : 'tap' }
        : { ...payload, endedAt: action.createdAt, endedAtSource: 'queue' },
    );
  },
  requestLeave: async (action) => {
    // The id stamped at the tap: a replay after a lost answer gets the same
    // request back from the server, never a second one.
    await requestLeave({
      ...(action.payload as unknown as RequestLeaveInput),
      expectedWorkerId: action.ownerUid ?? (action.payload.expectedWorkerId as string | undefined),
    });
  },
  postWorkerMessage: async (action) => {
    await postWorkerMessage(action.payload as unknown as WorkerMessageInput);
  },
  reportWorkException: async (action) => {
    await reportWorkException({
      ...(action.payload as unknown as ReportWorkExceptionInput),
      expectedWorkerId: action.ownerUid ?? (action.payload.expectedWorkerId as string | undefined),
    });
  },
  uploadTimesheetPhoto: async (action) => {
    if (!action.blob)
      throw { code: 'functions/failed-precondition', details: { reason: 'MEDIA_REMOVED' } };
    const file = new File([action.blob], action.blobName ?? 'photo.jpg', {
      type: action.blobType ?? 'image/jpeg',
    });
    const { jobId, timesheetId, type, uploadedBy, photoId } = action.payload as {
      jobId: string;
      timesheetId: string;
      type: Parameters<typeof uploadTimesheetPhoto>[0]['type'];
      uploadedBy: string;
      photoId?: string;
    };
    await uploadTimesheetPhoto({
      jobId,
      timesheetId,
      file,
      type,
      uploadedBy,
      expectedWorkerId: action.ownerUid ?? uploadedBy,
      photoId: photoId ?? action.id,
    });
  },
};

// ─── Subscription (for the offline indicator) ───────────────────────────────

/** A start the device has recorded but the server has not yet confirmed. */
export interface PendingStart {
  /** The queued action's id — what `discardQueuedAction` takes. */
  id: string;
  jobId: string;
  localRef: string;
  startedAt: string;
  /**
   * The server refused it for good. It will not be sent again on its own;
   * the worker has to retry or discard it (V1).
   */
  failed?: true;
  /** Why, as a code for `workerErrorText` — never the raw server prose. */
  errorCode?: string;
  /**
   * Set on a refused start: the unsent photos and expense/receipt
   * submissions that discarding it removes (see `queuedBehindStart`), so the
   * discard dialog can name them.
   */
  queuedBehind?: QueuedBehindCounts;
}

/** A queued `requestLeave` / `reportWorkException` (stage 1A-d). */
export interface PendingReport {
  /** The queued action's id — what `discardQueuedAction` takes. */
  id: string;
  type: 'requestLeave' | 'reportWorkException';
  /** reportWorkException: the report kind. */
  kind?: ReportWorkExceptionInput['kind'];
  jobId?: string;
  /** requestLeave: the leave type and range. */
  leaveType?: RequestLeaveInput['type'];
  from?: string;
  to?: string;
  /** When the worker tapped (ISO). */
  createdAt: string;
  /** The server refused it for good; the worker must retry or discard. */
  failed: boolean;
  errorCode?: string;
}

/** A queued worker message (UX 7.4). */
export interface PendingMessage {
  id: string;
  text: string;
  createdAt: string;
  failed: boolean;
  errorCode?: string;
}

export interface QueuedBehindCounts {
  photos: number;
  expenses: number;
}

/**
 * V1 — what discarding `start` removes besides itself: the same owner's
 * actions queued behind its `localRef` (finish, photos, expense/receipt
 * submissions). `discardQueuedAction` removes exactly these, and the discard
 * dialog's counts come from the same selection, so the two cannot disagree.
 * Anything that is not a start has nothing removed with it.
 */
export function queuedBehindStart(all: QueuedAction[], start: QueuedAction): QueuedAction[] {
  if (start.type !== 'startWorkDay' || !start.localRef) return [];
  const owner = queueOwner(start);
  return all.filter(
    (a) => a.id !== start.id && a.awaitsRef === start.localRef && queueOwner(a) === owner,
  );
}

/** The photos and expense/receipt submissions among `queuedBehindStart`. */
export function countQueuedBehind(actions: QueuedAction[]): QueuedBehindCounts {
  return {
    photos: actions.filter((a) => a.type === 'uploadTimesheetPhoto').length,
    expenses: actions.filter((a) => a.type === 'submitWorkerSubmission').length,
  };
}

export interface SyncQueueState {
  pending: number;
  failed: number;
  syncing: boolean;
  online: boolean;
  /**
   * Starts still sitting on the device. Kept in the shared state because the
   * worker's screen must NOT show one of these the way it shows a confirmed
   * day: "you are clocked in" and "your phone is holding a note saying you
   * clocked in" are different claims, and only one of them is true yet.
   */
  pendingStarts: PendingStart[];
  pendingCompletions: {
    jobId: string;
    timesheetId?: string;
    awaitsRef?: string;
    startedAt?: string;
    endedAt: string;
    /** 'chosen' when the worker picked the end themselves (1A-a). */
    endedAtSource?: EndedAtSource;
    failed: boolean;
  }[];
  /**
   * Stage 1A-d — reports and leave requests still on the device. "Olen täna
   * haige" offline must read "waiting to be sent", not "RM24 knows" (B6): the
   * UI reads this list, and only a server answer removes an entry.
   */
  pendingReports: PendingReport[];
  /** UX 7.4 — text messages still on the device; only a server answer removes one. */
  pendingMessages: PendingMessage[];
  unowned: number;
  issues: {
    id: string;
    type: string;
    createdAt: string;
    errorCode: string;
    unowned: boolean;
    /** The start's `localRef` this action waits behind, if any (A6: shown with that start). */
    awaitsRef?: string;
  }[];
}

let state: SyncQueueState = {
  pending: 0,
  failed: 0,
  syncing: false,
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
  pendingStarts: [],
  pendingCompletions: [],
  pendingReports: [],
  pendingMessages: [],
  unowned: 0,
  issues: [],
};
const listeners = new Set<(s: SyncQueueState) => void>();

function setState(patch: Partial<SyncQueueState>) {
  state = { ...state, ...patch };
  listeners.forEach((l) => l(state));
}

function startsFrom(all: QueuedAction[]): PendingStart[] {
  return (
    all
      // A receipt is a start that already went through, kept only so a late
      // dependant can still find its day. Reporting it as pending would tell
      // the worker their start is stuck for as long as the receipt is held.
      .filter(
        (a) =>
          a.type === 'startWorkDay' &&
          !a.completedAt &&
          // A start the server refused for good is reported even when a
          // finish waits behind it: that finish can never go through on its
          // own, so hiding the start would hide the only thing to resolve.
          (a.failedPermanently ||
            !all.some((done) => done.type === 'completeWorkDay' && done.awaitsRef === a.localRef)),
      )
      .map((a) => ({
        id: a.id,
        jobId: String(a.payload.jobId ?? ''),
        localRef: a.localRef ?? a.id,
        startedAt: String(a.payload.startedAt ?? a.createdAt),
        ...(a.failedPermanently
          ? {
              failed: true as const,
              errorCode: a.errorCode ?? 'unknown',
              queuedBehind: countQueuedBehind(queuedBehindStart(all, a)),
            }
          : {}),
      }))
  );
}

/** Exposed for the test that checks a receipt is not reported as pending. */
export const pendingStartsForTest = startsFrom;

function reportsFrom(all: QueuedAction[]): PendingReport[] {
  return all
    .filter(
      (a) => (a.type === 'requestLeave' || a.type === 'reportWorkException') && !a.completedAt,
    )
    .map((a) => ({
      id: a.id,
      type: a.type as PendingReport['type'],
      ...(a.type === 'reportWorkException'
        ? {
            kind: a.payload.kind as PendingReport['kind'],
            jobId: String(a.payload.jobId ?? ''),
          }
        : {
            leaveType: a.payload.type as PendingReport['leaveType'],
            from: String(a.payload.from ?? ''),
            to: String(a.payload.to ?? ''),
          }),
      createdAt: a.createdAt,
      failed: !!a.failedPermanently,
      ...(a.failedPermanently ? { errorCode: a.errorCode ?? 'unknown' } : {}),
    }));
}

/** Exposed for the queue tests. */
export const pendingReportsForTest = reportsFrom;

function messagesFrom(all: QueuedAction[]): PendingMessage[] {
  return all
    .filter((a) => a.type === 'postWorkerMessage' && !a.completedAt)
    .map((a) => ({
      id: a.id,
      text: String(a.payload.text ?? ''),
      createdAt: a.createdAt,
      failed: !!a.failedPermanently,
      ...(a.failedPermanently ? { errorCode: a.errorCode ?? 'unknown' } : {}),
    }));
}

async function refreshCounts(): Promise<QueuedAction[]> {
  const uid = getFirebase().auth.currentUser?.uid;
  const all = await ownedQueueStorage(baseStorage(), uid).list();
  const unknown = (await baseStorage().list()).filter((a) => !queueOwner(a) && !a.completedAt);
  const unowned = unknown.length;
  if (getFirebase().auth.currentUser?.uid !== uid) return [];
  setState({
    issues: uid
      ? failedActions(all).map((a) => ({
          id: a.id,
          type: a.type,
          createdAt: a.createdAt,
          errorCode: a.errorCode ?? 'unknown',
          unowned: !queueOwner(a),
          ...(a.awaitsRef ? { awaitsRef: a.awaitsRef } : {}),
        }))
      : [],
    pending: pendingActions(all).length,
    failed: failedActions(all).length,
    pendingStarts: startsFrom(all),
    pendingCompletions: all
      .filter((a) => a.type === 'completeWorkDay' && !a.completedAt)
      .map((a) => ({
        jobId: String(a.payload.jobId ?? ''),
        timesheetId: String(a.payload.timesheetId ?? ''),
        awaitsRef: a.awaitsRef,
        endedAt: String(a.payload.endedAt ?? a.createdAt),
        ...(a.payload.endedAtSource === 'chosen' ? { endedAtSource: 'chosen' as const } : {}),
        failed: !!a.failedPermanently,
        startedAt: a.awaitsRef
          ? String(all.find((start) => start.localRef === a.awaitsRef)?.payload.startedAt ?? '')
          : undefined,
      })),
    pendingReports: reportsFrom(all),
    pendingMessages: messagesFrom(all),
    unowned: uid ? unowned : 0,
  });
  return all;
}

// ─── Drainer ────────────────────────────────────────────────────────────────

let draining = false;

/**
 * Replay everything due.
 *
 * Starting a day is a callable now, so a completion queued behind one refers
 * to it by `localRef` and the drainer fills in the real id once the start
 * lands — see `drainQueue`. `waitForPendingWrites` is kept because other
 * direct Firestore writes (photos' metadata, profile edits) may still be in
 * Firestore's own offline queue when we come back.
 */
export async function drain(): Promise<void> {
  if (draining || loggingOut) return;
  if (typeof navigator !== 'undefined' && !navigator.onLine) return;

  draining = true;
  setState({ syncing: true });
  try {
    const all = await refreshCounts();
    if (pendingActions(all).length === 0) return;

    try {
      const { firestore } = getFirebase();
      // Bounded: offline this never settles, and we must not wedge the drainer.
      await Promise.race([
        waitForPendingWrites(firestore),
        new Promise((resolve) => setTimeout(resolve, 10_000)),
      ]);
    } catch {
      // Firestore not ready — the executors will fail and reschedule themselves.
    }

    const ownerUid = getFirebase().auth.currentUser?.uid;
    if (!ownerUid) return;
    const scoped = getStorage();
    const guarded = Object.fromEntries(
      Object.entries(executors).map(([key, execute]) => [
        key,
        async (action: QueuedAction) => {
          if (discarded.has(action.id)) return;
          if (
            loggingOut ||
            getFirebase().auth.currentUser?.uid !== ownerUid ||
            queueOwner(action) !== ownerUid
          )
            throw {
              code: 'unavailable',
              message: 'Account changed; waiting for the original account',
            };
          return execute(action);
        },
      ]),
    ) as ExecutorMap;
    await drainQueue(scoped, guarded, new Date());
    await refreshCounts();
  } finally {
    draining = false;
    setState({ syncing: false });
  }
}

/** Clear the failed flag and drain again — wired to the indicator's retry button. */
export async function retryFailedActions(): Promise<void> {
  await retryFailed(getStorage());
  await refreshCounts();
  await drain();
}

/**
 * Bootstrap. Called once at app start (main.tsx).
 *
 * KNOWN STAGE-1 LIMIT: Safari has no Background Sync, so replay only happens
 * while the app is in the foreground — the worker must open the app once back in
 * signal. Stage 2's CAP-3 native backend closes this. The `visibilitychange`
 * hook below is what makes "open the app" enough.
 */
export function startSyncQueue(): void {
  if (typeof window === 'undefined') return;

  getFirebase().auth.onAuthStateChanged(() => {
    loggingOut = false;
    setState({
      pending: 0,
      failed: 0,
      pendingStarts: [],
      pendingCompletions: [],
      pendingReports: [],
      unowned: 0,
      issues: [],
    });
    void refreshCounts().then(() => drain());
  });
  void requestPersistentStorage();
  void refreshCounts().then(() => drain());

  window.addEventListener('online', () => {
    setState({ online: true });
    void drain();
  });
  window.addEventListener('offline', () => setState({ online: false }));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void drain();
  });
  // Backstop for a long foreground session on flaky signal, where neither the
  // `online` event nor a visibility change ever fires.
  window.setInterval(() => void drain(), 60_000);
}

/** Subscribe to queue state. Returns an unsubscribe fn. */
export function subscribeSyncQueue(listener: (s: SyncQueueState) => void): () => void {
  listeners.add(listener);
  listener(state);
  return () => listeners.delete(listener);
}

export function useSyncQueue(): SyncQueueState {
  const [s, setS] = useState<SyncQueueState>(state);
  useEffect(() => subscribeSyncQueue(setS), []);
  return s;
}

// ─── Queue-aware wrappers (what the worker UI calls) ────────────────────────

async function queueAction(input: EnqueueInput, target = getStorage()): Promise<QueuedAction> {
  if (loggingOut && input.blob)
    throw { code: 'functions/permission-denied', details: { reason: 'WORKER_ACCOUNT_CHANGED' } };
  const action = await enqueue(target, input, newId(), new Date());
  await refreshCounts();
  return action;
}

const offline = () => typeof navigator !== 'undefined' && !navigator.onLine;

/**
 * Try the real call; queue it on a transient failure or when already offline.
 *
 * A PERMANENT failure is rethrown, never queued — a rules rejection or an
 * invalid argument would fail identically an hour later, and the worker deserves
 * the error now while they can still do something about it.
 *
 * Returns `queued: true` when the action was deferred, so the UI can say
 * "saved, will sync" instead of falsely reporting success.
 */
async function runOrQueue(
  input: EnqueueInput,
  run: () => Promise<void>,
): Promise<{ queued: boolean }> {
  const target = getStorage();
  if (offline()) {
    await queueAction(input, target);
    return { queued: true };
  }
  try {
    await run();
    return { queued: false };
  } catch (err) {
    if (isPermanentFailure(err)) throw err;
    await queueAction(input, target);
    return { queued: true };
  }
}

/**
 * Start a working day, online or not.
 *
 * Online, the server answers with the timesheet id and the day is real.
 * Offline, the device writes down that the worker started — and says so in
 * exactly those terms: `queued: true` with a `localRef` and no timesheet id,
 * because there is no day on the server yet and pretending otherwise is what
 * let two devices open two.
 *
 * Whatever the worker does next — finish, photograph — is queued against the
 * `localRef`, and the drainer rewrites it with the real id once the start
 * lands.
 */
export async function startWorkDayQueued(input: {
  continueTimesheetId?: string;
  jobId: string;
  location?: { latitude: number; longitude: number };
  startedAt?: string;
}): Promise<{ queued: boolean; timesheetId?: string; localRef: string }> {
  const target = getStorage();
  const ownerUid = getFirebase().auth.currentUser?.uid;
  const existing = await target.list();
  // A refused start is not an open day: it never reaches the server, so it
  // must not swallow a new tap (unchanged from before V1 reported it).
  const open = startsFrom(existing)
    .filter((s) => !s.failed)
    .at(-1);
  if (open) {
    if (open.jobId !== input.jobId)
      throw {
        code: 'functions/failed-precondition',
        message: 'Lõpeta kõigepealt eelmine tööpäev.',
      };
    return { queued: true, localRef: open.localRef };
  }
  const previousEnd = existing
    .filter((a) => a.type === 'completeWorkDay' && !a.completedAt && !a.failedPermanently)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    .at(-1);
  const localRef = newId();
  // Stable across every retry of THIS tap, so the server can recognise its
  // own earlier answer instead of inferring it.
  const payload = {
    ...input,
    startedAt: input.startedAt ?? new Date().toISOString(),
    clientRequestId: localRef,
    expectedWorkerId: ownerUid,
  };

  const queueIt = async () => {
    const action = await queueAction(
      {
        type: 'startWorkDay',
        payload: payload as unknown as Record<string, unknown>,
        // One start per job per tap-sequence: repeat taps collapse rather than
        // queueing two calls whose second one the server would reject.
        dedupeKey: `startWorkDay:${input.jobId}:${previousEnd?.id ?? 'open'}`,
        ...(previousEnd ? { afterActionId: previousEnd.id } : {}),
        localRef,
      },
      target,
    );
    return { queued: true, localRef: action.localRef ?? localRef };
  };

  if (offline() || previousEnd) return queueIt();
  try {
    const r = await startWorkDay(payload);
    return { queued: false, timesheetId: r.timesheetId, localRef };
  } catch (err) {
    // A conflict or a refusal is the server's answer, not a network problem:
    // it must reach the worker now, with what it says, rather than being
    // queued to fail again later.
    if (isPermanentFailure(err)) throw err;
    return queueIt();
  }
}

export function completeWorkDayQueued(
  /** `endedAtSource: 'chosen'` when the worker picked the end (1A-a, B1). */
  input: CompleteWorkDayQueuedInput,
  /** Set when the day was started offline and has no server id yet. */
  awaitsRef?: string,
): Promise<{ queued: boolean }> {
  // ONE end time, used by the immediate attempt, the queued copy and every
  // retry of that copy — so syncing late cannot move it, and a retry after a
  // lost response cannot either. Callers stamp it at the moment of the tap;
  // the fallback here is for a caller that did not, and is still earlier
  // than anything the server could infer.
  const withEndTime = {
    ...input,
    expectedWorkerId: getFirebase().auth.currentUser?.uid,
    endedAt: input.endedAt ?? new Date().toISOString(),
  };
  const queued = {
    type: 'completeWorkDay' as const,
    payload: withEndTime as unknown as Record<string, unknown>,
    dedupeKey: `completeWorkDay:${awaitsRef ?? withEndTime.timesheetId}`,
    ...(awaitsRef ? { awaitsRef } : {}),
  };
  // A day whose start has not reached the server cannot be completed against
  // it: there is no id to send. It goes straight to the queue, behind its own
  // start, rather than making a call that could only fail.
  if (awaitsRef) {
    return queueAction(queued).then(() => ({ queued: true }));
  }
  return runOrQueue(queued, async () => {
    await completeWorkDay(withEndTime);
  });
}

export function uploadTimesheetPhotoQueued(
  input: Parameters<typeof uploadTimesheetPhoto>[0],
  /** Set when the day was started offline and has no server id yet. */
  awaitsRef?: string,
): Promise<{ queued: boolean }> {
  const stableInput = {
    ...input,
    expectedWorkerId: getFirebase().auth.currentUser?.uid,
    photoId: input.photoId ?? crypto.randomUUID(),
  };
  const { file, ...rest } = stableInput;
  if (awaitsRef) {
    // Same reason as the completion: there is no timesheet to attach it to
    // yet. The bytes are kept and the link is made when the day is created.
    return queueAction({
      type: 'uploadTimesheetPhoto',
      payload: rest as unknown as Record<string, unknown>,
      blob: file,
      blobName: file.name,
      blobType: file.type,
      awaitsRef,
    }).then(() => ({ queued: true }));
  }
  return runOrQueue(
    {
      type: 'uploadTimesheetPhoto',
      payload: rest as unknown as Record<string, unknown>,
      blob: file,
      blobName: file.name,
      blobType: file.type,
      // No dedupeKey: two photos of the same type are two distinct photos.
    },
    async () => {
      await uploadTimesheetPhoto(stableInput);
    },
  );
}

/** Bytes and submission identity survive network failure and retries together. */
export function submitWorkerSubmissionQueued(
  input: SubmissionInput,
  file?: File,
  awaitsRef?: string,
): Promise<{ queued: boolean }> {
  const workerId = input.workerId ?? getFirebase().auth.currentUser?.uid;
  if (!workerId) throw { code: 'functions/unauthenticated' };
  const stableInput = { ...input, workerId };
  const action = {
    type: 'submitWorkerSubmission' as const,
    payload: stableInput as unknown as Record<string, unknown>,
    ...(file ? { blob: file, blobName: file.name, blobType: file.type } : {}),
    dedupeKey: `submission:${input.id}:${input.expectedRevision ?? 0}`,
    ...(awaitsRef ? { awaitsRef } : {}),
  };
  if (awaitsRef) return queueAction(action).then(() => ({ queued: true }));
  return runOrQueue(action, async () => {
    await submitWorkerSubmission(stableInput, file);
  });
}

export function requestTimeCorrectionQueued(
  input: Parameters<typeof requestTimeCorrection>[0],
): Promise<{ queued: boolean }> {
  const payload = { ...input, requestId: input.requestId ?? crypto.randomUUID() };
  return runOrQueue(
    {
      type: 'requestTimeCorrection',
      payload,
      dedupeKey: `requestTimeCorrection:${payload.requestId}`,
    },
    async () => {
      await requestTimeCorrection(payload);
    },
  );
}

/**
 * Stage 1A-d — ask for leave from the phone, online or not ("Olen täna haige").
 *
 * The `clientRequestId` is fixed at the tap and travels with every retry and
 * the queued copy, so the server answers a repeat with the same request.
 * `queued: true` means the request is still on the device: the UI says
 * "waiting to be sent", never "RM24 knows" (B6). Releasing the shift is RM24's
 * decision on approval — nothing here frees it.
 */
export function requestLeaveQueued(
  input: Omit<RequestLeaveInput, 'clientRequestId' | 'expectedWorkerId'> & {
    clientRequestId?: string;
  },
): Promise<{ queued: boolean }> {
  const clientRequestId = input.clientRequestId ?? newClientRequestId();
  const payload: RequestLeaveInput = {
    ...input,
    clientRequestId,
    expectedWorkerId: getFirebase().auth.currentUser?.uid,
  };
  return runOrQueue(
    {
      type: 'requestLeave',
      payload: payload as unknown as Record<string, unknown>,
      // The same leave asked twice offline is one request.
      dedupeKey: `requestLeave:${input.type}:${input.from}:${input.to}`,
    },
    async () => {
      await requestLeave(payload);
    },
  );
}

/**
 * Stage 1A-d — a structured report to RM24, online or not. The worker UI
 * calls `reportWorkExceptionQueued` in services/workerExceptions.ts, which
 * wraps this.
 *
 * One queued action per `clientRequestId`: a retry of the same tap reuses
 * the id (and collapses into the waiting action), while a NEW tap — e.g.
 * "15 min late" and later "30 min late" — is its own report, kept and sent.
 * The server dedupes by the same id. Changes no hours and no money.
 */
export function reportWorkExceptionViaQueue(
  input: Omit<ReportWorkExceptionInput, 'clientRequestId' | 'expectedWorkerId' | 'reportedAt'> & {
    clientRequestId?: string;
  },
): Promise<{ queued: boolean }> {
  const clientRequestId = input.clientRequestId ?? newClientRequestId();
  const reportedAt = new Date().toISOString();
  const payload: ReportWorkExceptionInput = {
    ...input,
    clientRequestId,
    reportedAt,
    expectedWorkerId: getFirebase().auth.currentUser?.uid,
  };
  return runOrQueue(
    {
      type: 'reportWorkException',
      payload: payload as unknown as Record<string, unknown>,
      dedupeKey: `reportWorkException:${clientRequestId}`,
    },
    async () => {
      await reportWorkException(payload);
    },
  );
}

/**
 * UX 7.4 — a worker's text message to RM24: sent now, or kept on the device
 * and sent when the signal returns. One id per written message: the server
 * derives the message document from it, so a replay after a lost answer is
 * not a second message. Attachments are not queued (they upload first, which
 * needs the network).
 */
export function postWorkerMessageQueued(input: {
  text: string;
  clientMessageId: string;
}): Promise<{ queued: boolean }> {
  return runOrQueue(
    {
      type: 'postWorkerMessage',
      payload: { ...input },
      dedupeKey: `postWorkerMessage:${input.clientMessageId}`,
    },
    async () => {
      await postWorkerMessage(input);
    },
  );
}

/** Called only from the worker UI; existing-sheet ownership is checked by each server operation. */
export async function recoverLegacyQueue(): Promise<void> {
  const uid = getFirebase().auth.currentUser?.uid;
  if (!uid) return;
  const target = getStorage();
  await recoverLegacyActions({ ...baseStorage(), put: target.put }, uid, async (action) => {
    try {
      const { jobId, timesheetId } = action.payload;
      if (
        typeof jobId !== 'string' ||
        typeof timesheetId !== 'string' ||
        jobId.includes('/') ||
        timesheetId.includes('/')
      )
        return false;
      const snapshot = await getDoc(
        doc(getFirebase().firestore, `jobs/${jobId}/timesheets/${timesheetId}`),
      );
      return (
        snapshot.exists() &&
        snapshot.data().workerId === uid &&
        getFirebase().auth.currentUser?.uid === uid
      );
    } catch {
      return false;
    }
  });
  await refreshCounts();
  await drain();
}
export async function discardQueuedAction(id: string): Promise<void> {
  const uid = getFirebase().auth.currentUser?.uid;
  if (!uid) return;
  const action = (await baseStorage().list()).find((a) => a.id === id);
  if (!action || queueOwner(action) !== uid) return;
  discarded.add(id);
  await baseStorage().remove(id);
  if (action.type === 'startWorkDay') {
    // V1 — the worker discarded a start: its day will never exist, so what
    // was queued behind it goes with it, as the discard dialog lists.
    // Marking it failed instead left a finish that "Proovi uuesti" could
    // only fail again, forever.
    for (const child of queuedBehindStart(await baseStorage().list(), action)) {
      discarded.add(child.id);
      await baseStorage().remove(child.id);
    }
  } else if (action.localRef)
    for (const child of await baseStorage().list()) {
      if (child.awaitsRef === action.localRef && queueOwner(child) === uid)
        await baseStorage().put({
          ...child,
          failedPermanently: true,
          errorCode: 'DEPENDENCY_FAILED',
        });
    }
  await refreshCounts();
  await drain();
}
export async function purgeQueueMediaForSignOut(): Promise<void> {
  loggingOut = true;
  mediaEpoch++;
  for (const action of await baseStorage().list()) {
    const safe = withoutMedia(action);
    if (safe) await baseStorage().put(safe);
    else {
      discarded.add(action.id);
      await baseStorage().remove(action.id);
    }
  }
  await refreshCounts();
}

export async function hasUnsentQueueMedia(): Promise<boolean> {
  return (await getStorage().list()).some((a) => !a.completedAt && !!a.blob);
}
